import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

export default function ImageUpload({
  value = null,
  onChange,
  multiple = false,
  maxSize = 5 * 1024 * 1024, // 5MB default
  className = '',
  label = 'Upload Image',
}) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      const file = files[0];

      // Validate file size
      if (file.size > maxSize) {
        toast.error(`File size must be less than ${maxSize / 1024 / 1024}MB`);
        setUploading(false);
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        setUploading(false);
        return;
      }

      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        if (multiple) {
          const currentImages = Array.isArray(value) ? value : [];
          onChange([...currentImages, reader.result]);
        } else {
          onChange(reader.result);
        }
        setUploading(false);
        toast.success('Image uploaded!');
      };
      reader.onerror = () => {
        toast.error('Failed to read file');
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
      setUploading(false);
    }
  };

  const handleRemove = (e, indexToRemove = null) => {
    e.stopPropagation();
    if (multiple && indexToRemove !== null) {
      const newImages = value.filter((_, idx) => idx !== indexToRemove);
      onChange(newImages);
    } else {
      onChange(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={className}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFileSelect(e.target.files)}
        multiple={multiple}
        data-testid="image-upload-input"
      />

      {/* Single image preview */}
      {!multiple && value && (
        <div className="relative inline-block">
          <img
            src={value}
            alt="Uploaded"
            className="w-32 h-32 object-cover rounded-lg border-2 border-border"
            data-testid="uploaded-image"
          />
          <button
            type="button"
            onClick={handleRemove}
            className="absolute -top-2 -right-2 w-7 h-7 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
            data-testid="remove-image-button"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Multiple images preview */}
      {multiple && Array.isArray(value) && value.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          {value.map((img, idx) => (
            <div key={idx} className="relative">
              <img
                src={img}
                alt={`Upload ${idx + 1}`}
                className="w-full h-24 object-cover rounded-lg border-2 border-border"
              />
              <button
                type="button"
                onClick={(e) => handleRemove(e, idx)}
                className="absolute -top-2 -right-2 w-6 h-6 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload area */}
      {(!value || multiple) && (
        <div
          onClick={openFileDialog}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`upload-area p-6 rounded-lg flex flex-col items-center justify-center text-center ${dragOver ? 'dragover' : ''}`}
          data-testid="upload-area"
        >
          {uploading ? (
            <Loader2 className="w-8 h-8 text-primary animate-spin mb-2" />
          ) : (
            <Upload className="w-8 h-8 text-muted-foreground mb-2" />
          )}
          <p className="text-sm font-medium mb-1">{label}</p>
          <p className="text-xs text-muted-foreground">
            Drag & drop or click to upload
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Max {maxSize / 1024 / 1024}MB
          </p>
        </div>
      )}
    </div>
  );
}
